import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectClient,
  selectMaybeClientPartner,
  selectTraditionalLTCQuotesError,
  selectTraditionalLTCQuotesIsLoading,
  postTraditionalLTCQuotesQueryRequest,
} from '@/model';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { calculateAge, isTobacco, zipCodeToState } from '@shared';
import { getMaritalClassForTraditionalLTC, type PaymentOption } from '@/features/shop-insurance';

interface UseTraditionalLTCQuoteFetchingProps {
  open: boolean;
  paymentOption: PaymentOption;
  selectedBuyerOption: {
    key: '1-buyer' | '2-buyer';
    label: string;
    maritalClass: string;
  };
  hasPartnerWithDemographics: boolean;
  advisorId: string;
}

const ELIGIBLE_PAYMENT_OPTIONS = ['Annually', 'Monthly'];

export function useTraditionalLTCQuoteFetching({
  open,
  paymentOption,
  selectedBuyerOption,
  hasPartnerWithDemographics,
  advisorId,
}: UseTraditionalLTCQuoteFetchingProps) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const clientPartner = useSelector(selectMaybeClientPartner);
  const isLoadingTraditionalLTC = useSelector(selectTraditionalLTCQuotesIsLoading);
  const traditionalLTCError = useSelector(selectTraditionalLTCQuotesError);
  const { flags } = useFeatureFlags();

  const fetchedQuoteConfigs = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (open && client?.intakeSurvey && !isLoadingTraditionalLTC) {
      const eligiblePaymentOptions = ELIGIBLE_PAYMENT_OPTIONS;

      const cacheKey =
        hasPartnerWithDemographics && eligiblePaymentOptions.includes(paymentOption)
          ? `${paymentOption}-${selectedBuyerOption.key}`
          : paymentOption;

      if (
        eligiblePaymentOptions.includes(paymentOption) &&
        !fetchedQuoteConfigs.current.has(cacheKey)
      ) {
        fetchedQuoteConfigs.current.add(cacheKey);

        const dateOfBirth = new Date(client.intakeSurvey.clientBirthDate);
        const age = calculateAge(dateOfBirth);

        const clientTobaccoStatus = isTobacco(client.intakeSurvey) ? 'Tobacco' : 'Non-Tobacco';
        const underwritingClass = clientTobaccoStatus === 'Tobacco' ? 'Standard' : 'Preferred';
        const gender = client.intakeSurvey.clientGenderAtBirth === 'Male' ? 'Male' : 'Female';
        const state = zipCodeToState(client.intakeSurvey.clientZipCode);
        const usaState =
          flags.nycLifeDemo && state.stateAbbreviation === 'NY' ? 'NJ' : state.stateAbbreviation;

        const maritalClass = getMaritalClassForTraditionalLTC(
          paymentOption,
          selectedBuyerOption,
          hasPartnerWithDemographics,
          client.intakeSurvey.clientMaritalStatus,
        );

        const payload = {
          primaryAge: age,
          primaryGender: gender,
          maritalClass: maritalClass,
          usaState,
          premiumFrequency:
            paymentOption === 'Annually'
              ? 'Annually'
              : paymentOption === 'Monthly'
                ? 'Monthly'
                : 'Annually',
          underwritingClass: underwritingClass,
          advisorId: advisorId,
        };

        dispatch(postTraditionalLTCQuotesQueryRequest(payload));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    client,
    clientPartner,
    paymentOption,
    selectedBuyerOption,
    isLoadingTraditionalLTC,
    advisorId,
  ]);

  useEffect(() => {
    if (traditionalLTCError) {
      fetchedQuoteConfigs.current.clear();
    }
  }, [traditionalLTCError]);

  useEffect(() => {
    fetchedQuoteConfigs.current.clear();
  }, [selectedBuyerOption, hasPartnerWithDemographics, paymentOption]);

  return {
    isLoadingTraditionalLTC,
    traditionalLTCError,
  };
}
